import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HeartIcon,
  CakeIcon,
  ChatBubbleBottomCenterTextIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { culturalAPI } from '../services/api';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

type CulturalTab = 'etiquette' | 'food' | 'language' | 'festivals' | 'safety';

interface TabConfig {
  id: CulturalTab;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
}

const TABS: TabConfig[] = [
  { id: 'etiquette', name: 'Cultural Norms', icon: HeartIcon, description: 'Greetings, dining, dress, and etiquette' },
  { id: 'food', name: 'Food & Drink', icon: CakeIcon, description: 'Must-try dishes, street food, and customs' },
  { id: 'language', name: 'Language Basics', icon: ChatBubbleBottomCenterTextIcon, description: 'Essential phrases and common signs' },
  { id: 'festivals', name: 'Festivals & Events', icon: CalendarDaysIcon, description: 'Cultural events and national holidays' },
  { id: 'safety', name: 'Safety & Health', icon: ShieldCheckIcon, description: 'Safety ratings, scams, healthcare, vaccines' },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DIETARY_OPTIONS = ['vegetarian', 'vegan', 'halal', 'kosher', 'gluten-free', 'lactose-free', 'nut-free'];

const SAFETY_COLORS: Record<string, string> = {
  'Very Safe': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'Safe': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'Moderate': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  'Exercise Caution': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  'Avoid': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export const CulturalIntelligenceHub: React.FC = () => {
  const { t } = useTranslation(['cultural', 'common']);
  const [activeTab, setActiveTab] = useState<CulturalTab>('etiquette');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Common
  const [destination, setDestination] = useState('');
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string[]>([]);
  const [travelMonth, setTravelMonth] = useState('');

  // Results
  const [etiquetteResult, setEtiquetteResult] = useState<any>(null);
  const [foodResult, setFoodResult] = useState<any>(null);
  const [languageResult, setLanguageResult] = useState<any>(null);
  const [festivalsResult, setFestivalsResult] = useState<any>(null);
  const [safetyResult, setSafetyResult] = useState<any>(null);

  const toggleDietary = (item: string) => {
    setDietaryRestrictions(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!destination.trim()) return;
    setLoading(true); setError(null);
    try {
      switch (activeTab) {
        case 'etiquette':
          setEtiquetteResult(await culturalAPI.etiquette({ destination: destination.trim() }));
          break;
        case 'food':
          setFoodResult(await culturalAPI.foodDrink({ destination: destination.trim(), dietary_restrictions: dietaryRestrictions.length > 0 ? dietaryRestrictions : undefined }));
          break;
        case 'language':
          setLanguageResult(await culturalAPI.language({ destination: destination.trim() }));
          break;
        case 'festivals':
          setFestivalsResult(await culturalAPI.festivals({ destination: destination.trim(), travel_month: travelMonth || undefined }));
          break;
        case 'safety':
          setSafetyResult(await culturalAPI.safety({ destination: destination.trim() }));
          break;
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to get cultural info. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderArray = (arr: any[]) => {
    if (!arr || arr.length === 0) return null;
    return <ul className="list-disc list-inside space-y-1">{arr.map((item, i) => <li key={i} className="text-gray-700 dark:text-gray-300">{String(item)}</li>)}</ul>;
  };

  const renderKV = (obj: any) => {
    if (!obj || typeof obj !== 'object') return null;
    return (
      <div className="space-y-2">
        {Object.entries(obj).map(([key, val]: [string, any]) => (
          <p key={key} className="text-gray-700 dark:text-gray-300">
            <strong className="capitalize">{key.replace(/_/g, ' ')}:</strong>{' '}
            {Array.isArray(val) ? val.join(', ') : typeof val === 'boolean' ? (val ? 'Yes' : 'No') : typeof val === 'object' ? JSON.stringify(val) : String(val)}
          </p>
        ))}
      </div>
    );
  };

  const inputCls = "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent";
  const btnCls = "flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
  const cardCls = "bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700";

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <LoadingOverlay isVisible={loading} message="Researching cultural information..." subMessage="Our AI is compiling expert guidance" />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <HeartIcon className="h-7 w-7 text-cyan-600" />
          {t('cultural:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Cultural insights, language help, and safety information</p>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-4 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setError(null); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.id ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'}`}>
                <Icon className="h-5 w-5" />{tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className={cardCls}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className={activeTab === 'food' || activeTab === 'festivals' ? '' : 'md:col-span-2'}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cultural:labels.destination')}</label>
            <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g., Japan, Tokyo, Thailand" className={inputCls} />
          </div>
          {activeTab === 'festivals' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cultural:labels.travelMonth')} (optional)</label>
              <select value={travelMonth} onChange={(e) => setTravelMonth(e.target.value)} className={inputCls}>
                <option value="">Any month</option>
                {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}
        </div>
        {activeTab === 'food' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('cultural:labels.dietaryRestrictions')} (optional)</label>
            <div className="flex flex-wrap gap-2">
              {DIETARY_OPTIONS.map(d => (
                <button key={d} type="button" onClick={() => toggleDietary(d)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${dietaryRestrictions.includes(d) ? 'bg-cyan-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}
        <button type="submit" disabled={loading || !destination.trim()} className={btnCls}>
          <MagnifyingGlassIcon className="h-5 w-5" />Search
        </button>
      </form>

      {/* Results */}
      <div className="mt-6 space-y-4">
        {/* Etiquette Results */}
        {activeTab === 'etiquette' && etiquetteResult && (
          <>
            {etiquetteResult.greetings && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.greetings')}</h3>{renderKV(etiquetteResult.greetings)}</div>}
            {etiquetteResult.dining && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.dining')}</h3>{renderKV(etiquetteResult.dining)}</div>}
            {etiquetteResult.dress_code && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.dressCode')}</h3>{renderKV(etiquetteResult.dress_code)}</div>}
            {etiquetteResult.photography && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.photography')}</h3>{renderKV(etiquetteResult.photography)}</div>}
            {etiquetteResult.gifts && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.gifts')}</h3>{renderKV(etiquetteResult.gifts)}</div>}
            {etiquetteResult.religion && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.religion')}</h3>{renderKV(etiquetteResult.religion)}</div>}
            {etiquetteResult.taboos && <div className={`${cardCls} bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800`}><h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-3">{t('cultural:sections.taboos')}</h3>{renderArray(etiquetteResult.taboos)}</div>}
            {(etiquetteResult.dos || etiquetteResult.donts) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {etiquetteResult.dos && (
                  <div className={`${cardCls} bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800`}>
                    <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
                      <CheckCircleIcon className="h-5 w-5" />{t('cultural:sections.dos')}
                    </h3>
                    {renderArray(etiquetteResult.dos)}
                  </div>
                )}
                {etiquetteResult.donts && (
                  <div className={`${cardCls} bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800`}>
                    <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-3 flex items-center gap-2">
                      <ExclamationCircleIcon className="h-5 w-5" />{t('cultural:sections.donts')}
                    </h3>
                    {renderArray(etiquetteResult.donts)}
                  </div>
                )}
              </div>
            )}
            {etiquetteResult.business_etiquette && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.businessEtiquette')}</h3>{renderKV(etiquetteResult.business_etiquette)}</div>}
          </>
        )}

        {/* Food & Drink Results */}
        {activeTab === 'food' && foodResult && (
          <>
            {foodResult.must_try_dishes && (
              <div className={cardCls}>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.mustTryDishes')}</h3>
                <div className="space-y-4">
                  {foodResult.must_try_dishes.map((d: any, i: number) => (
                    <div key={i} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">{d.name}</h4>
                        {d.local_name && <span className="text-sm italic text-cyan-600 dark:text-cyan-400">({d.local_name})</span>}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{d.description}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm">
                        {d.price_range && <span className="text-cyan-600 dark:text-cyan-400">{d.price_range}</span>}
                        {d.dietary_info && <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-gray-700 dark:text-gray-300 text-xs">{d.dietary_info}</span>}
                      </div>
                      {d.where_to_find && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{d.where_to_find}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {foodResult.street_food && (
              <div className={cardCls}>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.streetFood')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {foodResult.street_food.map((s: any, i: number) => (
                    <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="font-medium text-gray-900 dark:text-white">{s.name} {s.price && <span className="text-cyan-600 dark:text-cyan-400 text-sm">{s.price}</span>}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{s.description}</p>
                      {s.where && <p className="text-sm text-gray-500 dark:text-gray-400">{s.where}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {foodResult.dietary_info && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.dietaryInfo')}</h3>{renderKV(foodResult.dietary_info)}</div>}
            {foodResult.beverages && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.beverages')}</h3>{renderKV(foodResult.beverages)}</div>}
            {foodResult.ordering_customs && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.orderingCustoms')}</h3>{renderKV(foodResult.ordering_customs)}</div>}
            {foodResult.markets && (
              <div className={cardCls}>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.markets')}</h3>
                <div className="space-y-3">
                  {foodResult.markets.map((m: any, i: number) => (
                    <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="font-medium text-gray-900 dark:text-white">{m.name} <span className="text-sm text-cyan-600 dark:text-cyan-400">{m.type}</span></p>
                      {m.best_time && <p className="text-sm text-gray-600 dark:text-gray-400">Best time: {m.best_time}</p>}
                      {m.highlights && <p className="text-sm text-gray-500 dark:text-gray-400">{m.highlights}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {foodResult.water_safety && (
              <div className={`${cardCls} bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800`}>
                <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-3">{t('cultural:sections.waterSafety')}</h3>
                {renderKV(foodResult.water_safety)}
              </div>
            )}
            {foodResult.food_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.foodTips')}</h3>{renderArray(foodResult.food_tips)}</div>}
          </>
        )}

        {/* Language Results */}
        {activeTab === 'language' && languageResult && (
          <>
            <div className={cardCls}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {languageResult.primary_language && <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"><p className="text-sm text-gray-500 dark:text-gray-400">Primary Language</p><p className="text-lg font-bold text-gray-900 dark:text-white">{languageResult.primary_language}</p></div>}
                {languageResult.english_proficiency && <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"><p className="text-sm text-gray-500 dark:text-gray-400">English Level</p><p className="text-lg font-bold text-gray-900 dark:text-white">{languageResult.english_proficiency}</p></div>}
              </div>
              {languageResult.other_languages && <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">Other languages: {languageResult.other_languages.join(', ')}</p>}
              {languageResult.alphabet_info && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{languageResult.alphabet_info}</p>}
            </div>

            {languageResult.phrases && (
              <div className={cardCls}>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.phrases')}</h3>
                {Object.entries(languageResult.phrases).map(([category, phrases]: [string, any]) => (
                  <div key={category} className="mb-4">
                    <h4 className="text-sm font-medium text-cyan-600 dark:text-cyan-400 uppercase mb-2">{category}</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-700/50">
                            <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">English</th>
                            <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Local</th>
                            <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Pronunciation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(phrases) && phrases.map((p: any, i: number) => (
                            <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{p.english}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">{p.local}</td>
                              <td className="px-3 py-2 text-gray-500 dark:text-gray-400 italic">{p.pronunciation}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {languageResult.common_signs && (
              <div className={cardCls}>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.commonSigns')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {languageResult.common_signs.map((s: any, i: number) => (
                    <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center">
                      <p className="font-medium text-gray-900 dark:text-white">{s.sign}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{s.meaning}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {languageResult.translation_apps && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.translationApps')}</h3><div className="space-y-2">{languageResult.translation_apps.map((a: any, i: number) => <p key={i} className="text-gray-700 dark:text-gray-300"><strong>{a.name}:</strong> {a.features} {a.offline && <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs ml-1">Offline</span>}</p>)}</div></div>}
            {languageResult.communication_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.communicationTips')}</h3>{renderArray(languageResult.communication_tips)}</div>}
          </>
        )}

        {/* Festivals Results */}
        {activeTab === 'festivals' && festivalsResult && (
          <>
            {festivalsResult.major_festivals && (
              <div className={cardCls}>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.majorFestivals')}</h3>
                <div className="space-y-4">
                  {festivalsResult.major_festivals.map((f: any, i: number) => (
                    <div key={i} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">{f.name}</h4>
                        {f.type && <span className="px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded text-xs">{f.type}</span>}
                        {f.month && <span className="text-sm text-gray-500 dark:text-gray-400">{f.month}</span>}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{f.description}</p>
                      {f.duration && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Duration: {f.duration}</p>}
                      {f.participation && <p className="text-sm text-gray-500 dark:text-gray-400">{f.participation}</p>}
                      {f.impact_on_travel && <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">{f.impact_on_travel}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {festivalsResult.national_holidays && (
              <div className={cardCls}>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.nationalHolidays')}</h3>
                <div className="space-y-2">
                  {festivalsResult.national_holidays.map((h: any, i: number) => (
                    <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{h.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{h.description}</p>
                        {h.closures && <p className="text-sm text-orange-600 dark:text-orange-400">{h.closures}</p>}
                      </div>
                      {h.date && <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap ml-4">{h.date}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {festivalsResult.seasonal_highlights && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.seasonalHighlights')}</h3>{renderKV(festivalsResult.seasonal_highlights)}</div>}
            {festivalsResult.participation_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.participationTips')}</h3>{renderArray(festivalsResult.participation_tips)}</div>}
            {festivalsResult.booking_advice && <div className={`${cardCls} bg-cyan-50 dark:bg-cyan-900/10 border-cyan-200 dark:border-cyan-800`}><p className="text-cyan-800 dark:text-cyan-300">{festivalsResult.booking_advice}</p></div>}
          </>
        )}

        {/* Safety Results */}
        {activeTab === 'safety' && safetyResult && (
          <>
            {safetyResult.overall_safety && (
              <div className={cardCls}>
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('cultural:sections.overallSafety')}</h3>
                  {safetyResult.overall_safety.rating && (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${SAFETY_COLORS[safetyResult.overall_safety.rating] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {safetyResult.overall_safety.rating}
                    </span>
                  )}
                </div>
                <p className="text-gray-700 dark:text-gray-300">{safetyResult.overall_safety.description}</p>
                {safetyResult.overall_safety.solo_travel && <p className="text-sm text-gray-600 dark:text-gray-400 mt-2"><strong>Solo travel:</strong> {safetyResult.overall_safety.solo_travel}</p>}
                {safetyResult.overall_safety.women_travelers && <p className="text-sm text-gray-600 dark:text-gray-400"><strong>Women travelers:</strong> {safetyResult.overall_safety.women_travelers}</p>}
                {safetyResult.overall_safety.lgbtq && <p className="text-sm text-gray-600 dark:text-gray-400"><strong>LGBTQ+:</strong> {safetyResult.overall_safety.lgbtq}</p>}
              </div>
            )}
            {safetyResult.common_scams && (
              <div className={`${cardCls} bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800`}>
                <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-3">{t('cultural:sections.commonScams')}</h3>
                <div className="space-y-3">
                  {safetyResult.common_scams.map((s: any, i: number) => (
                    <div key={i} className="p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                      <p className="font-medium text-red-800 dark:text-red-300">{s.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{s.description}</p>
                      <p className="text-sm text-green-700 dark:text-green-400 mt-1"><strong>Prevention:</strong> {s.how_to_avoid}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {safetyResult.emergency_numbers && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.emergencyNumbers')}</h3>{renderKV(safetyResult.emergency_numbers)}</div>}
            {safetyResult.healthcare && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.healthcare')}</h3>{renderKV(safetyResult.healthcare)}</div>}
            {safetyResult.vaccinations && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.vaccinations')}</h3>{renderKV(safetyResult.vaccinations)}</div>}
            {safetyResult.health_kit && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.healthKit')}</h3>{renderArray(safetyResult.health_kit)}</div>}
            {safetyResult.natural_hazards && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.naturalHazards')}</h3>{renderArray(safetyResult.natural_hazards)}</div>}
            {safetyResult.areas_to_avoid && <div className={`${cardCls} bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800`}><h3 className="text-lg font-semibold text-orange-800 dark:text-orange-300 mb-3">{t('cultural:sections.areasToAvoid')}</h3>{renderArray(safetyResult.areas_to_avoid)}</div>}
            {safetyResult.safety_tips && <div className={cardCls}><h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('cultural:sections.safetyTips')}</h3>{renderArray(safetyResult.safety_tips)}</div>}
          </>
        )}
      </div>

      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">{t('common:disclaimer.travel')}</p>
      </div>
    </div>
  );
};

export default CulturalIntelligenceHub;
